import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { BaseApiService } from '@bo/ng-api';

/**
 * Custom PrepRequest for transaction receipts
 */
export interface TransactionReceiptPrepRequest {
  file_name: string;
  file_type: string;
  file_size: number;
  transaction: number;
}

/**
 * PrepResponse from the backend
 * Returns an upload ID and S3 params.
 *
 * NOTE: `params` contains the S3 presigned POST `url` plus a `fields` object
 * with the exact form fields allowed by the signed policy. `params` also
 * carries extra metadata (`meta`, `download_url`, and top-level duplicates
 * of the signed fields) that must NOT be sent to S3 - only `params.fields`
 * should be used as the upload form body.
 */
export interface TransactionReceiptPrepResponse {
  id: string; // UUID for the upload
  params: {
    url: string; // S3 bucket URL to POST to
    fields: Record<string, string | number>; // Exact form fields allowed by the signed policy
    download_url?: string;
    meta?: Record<string, unknown>;
    [key: string]: unknown; // Additional top-level metadata we should ignore
  };
}

/**
 * Complete response
 */
export interface TransactionReceiptCompleteResponse {
  // Define based on what your backend returns
  success?: boolean;
  message?: string;
}

/**
 * Custom UploadsApiService specifically for transaction receipts
 *
 * Handles the 3-step upload flow:
 * 1. Prep - Get upload ID and S3 params
 * 2. Upload to S3 (handled by ngx-uploader)
 * 3. Complete - Notify backend that upload is done
 */
@Injectable()
export class ReceiptUploadsApiService extends BaseApiService {
  protected override http: HttpClient;
  override baseUrl: string;

  private readonly URL_TRANSACTIONS_RECEIPT_UPLOAD = 'transactions/receipt-upload';
  private readonly URL_TRANSACTIONS_RECEIPT_UPLOAD_PREP = 'transactions/receipt-upload/prep';
  private readonly URL_COMPLETE = 'complete'; // relative to URL_TRANSACTIONS_RECEIPT_UPLOAD

  constructor() {
    const http = inject(HttpClient);
    super(http);

    this.http = http;
    this.baseUrl = environment.apiRoot;
  }

  /**
   * Step 1: Prepare the receipt upload
   * Returns upload ID and S3 parameters
   */
  prepareReceiptUpload(request: TransactionReceiptPrepRequest): Observable<TransactionReceiptPrepResponse> {
    return this.http.post<TransactionReceiptPrepResponse>(
      this.getUrl(this.URL_TRANSACTIONS_RECEIPT_UPLOAD_PREP, true),
      request,
    );
  }

  /**
   * Step 3: Mark receipt upload as complete
   * Called after successful S3 upload
   *
   * @param uploadId - The ID returned from prepareReceiptUpload
   */
  completeReceiptUpload(uploadId: string): Observable<TransactionReceiptCompleteResponse> {
    return this.http.put<TransactionReceiptCompleteResponse>(
      this.getUrl([this.URL_TRANSACTIONS_RECEIPT_UPLOAD, uploadId, this.URL_COMPLETE], true),
      {},
    );
  }

  /**
   * Delete transaction receipt
   *
   * @param id - Receipt upload ID
   * @return Observable<Record<PropertyKey, never>>
   */
  deleteTransactionReceipt(id: string): Observable<Record<PropertyKey, never>> {
    const url = this.getUrl([this.URL_TRANSACTIONS_RECEIPT_UPLOAD, id], true);
    return this.http.delete<Record<PropertyKey, never>>(url);
  }
}
